import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkles, TrendingUp, FileText, CheckCircle2, Clock } from "lucide-react";

function StatCard({ label, value, hint, icon: Icon }) {
    return (
        <div className="lk-border lk-surface rounded-xl p-5 sm:p-6" data-testid={`dash-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-start justify-between">
                <div className="lk-label">{label}</div>
                {Icon && <Icon className="w-4 h-4 text-[var(--lk-primary)] opacity-60" />}
            </div>
            <div className="mt-3 font-heading text-3xl sm:text-4xl text-[var(--lk-primary)]">{value}</div>
            {hint && <div className="text-xs text-[var(--lk-muted)] mt-2">{hint}</div>}
        </div>
    );
}

const PLATFORM_LABELS = { linkedin: "LinkedIn", twitter: "X", reddit: "Reddit", instagram: "Instagram" };

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [recentRuns, setRecentRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [statsRes, historyRes] = await Promise.all([
                    api.get("/dashboard"),
                    api.get("/history?limit=5").catch(() => ({ data: [] })),
                ]);
                setStats(statsRes.data);
                setRecentRuns(Array.isArray(historyRes.data) ? historyRes.data : []);
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const firstName = user?.name?.split(" ")[0] || "there";

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 sm:mb-10">
                    <div>
                        <div className="lk-label">Good to have you back</div>
                        <h1 className="font-heading text-3xl sm:text-5xl mt-2 tracking-tight">
                            Hey {firstName} <span className="text-[var(--lk-primary)]">✦</span>
                        </h1>
                        <p className="text-[var(--lk-muted)] mt-2 text-sm sm:text-base">Here's what Lakshmi has been up to.</p>
                    </div>
                    <Link
                        to="/workspace"
                        className="lk-btn-primary rounded-full px-5 py-3 flex items-center gap-2 text-sm w-full sm:w-auto justify-center shrink-0"
                    >
                        <Sparkles className="w-4 h-4" /> Write a new post
                    </Link>
                </div>

                {loading ? (
                    <div className="mt-10 text-[var(--lk-muted)]" data-testid="dash-loading">Loading your stats…</div>
                ) : err ? (
                    <div className="mt-10 text-red-400 text-sm" data-testid="dash-error">{err}</div>
                ) : stats ? (
                    <>
                        {/* Stats grid — user-meaningful metrics only */}
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            <StatCard
                                label="Posts generated"
                                value={stats.total_generations ?? "—"}
                                hint="All-time total"
                                icon={FileText}
                            />
                            <StatCard
                                label="Published"
                                value={stats.successful_generations ?? "—"}
                                hint="Made it to copy/schedule"
                                icon={CheckCircle2}
                            />
                            <StatCard
                                label="Avg quality score"
                                value={stats.avg_quality_score ? `${Math.round(stats.avg_quality_score)}/100` : "—"}
                                hint="Lakshmi's editor verdict"
                                icon={TrendingUp}
                            />
                            <StatCard
                                label="Success rate"
                                value={stats.success_rate ? `${Math.round(stats.success_rate * 100)}%` : "—"}
                                hint="Clean runs, no errors"
                                icon={Sparkles}
                            />
                        </div>

                        {/* Recent posts */}
                        <div className="mt-8 sm:mt-10">
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-heading text-2xl">Recent posts</div>
                                <Link to="/history" className="text-xs text-[var(--lk-primary)] hover:underline flex items-center gap-1">
                                    View all <ArrowRight className="w-3 h-3" />
                                </Link>
                            </div>

                            {recentRuns.length === 0 ? (
                                <div className="lk-border lk-surface rounded-xl p-8 sm:p-10 text-center">
                                    <img
                                        src="/image/lakshmi_bg.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="h-32 sm:h-40 w-auto mx-auto mb-4"
                                        style={{ maskImage: "linear-gradient(to top, transparent 0%, black 30%)", WebkitMaskImage: "linear-gradient(to top, transparent 0%, black 30%)" }}
                                    />
                                    <p className="text-[var(--lk-muted)] text-sm">No posts yet. Lakshmi's ready when you are.</p>
                                    <Link to="/workspace" className="mt-4 inline-flex items-center gap-2 lk-btn-primary rounded-full px-5 py-2.5 text-sm">
                                        Write your first post <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            ) : (
                                <div className="lk-border lk-surface rounded-xl overflow-hidden">
                                    {recentRuns.map((run, i) => (
                                        <Link
                                            key={run.id}
                                            to={`/history/${run.id}`}
                                            className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-[var(--lk-surface-2)] transition-colors ${i > 0 ? "border-t border-[var(--lk-border)]" : ""}`}
                                            data-testid={`recent-run-${run.id}`}
                                        >
                                            <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--lk-surface-2)] lk-border flex items-center justify-center">
                                                <FileText className="w-4 h-4 text-[var(--lk-primary)]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm truncate">{run.prompt || "Untitled post"}</div>
                                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                    {run.platforms?.map(p => (
                                                        <span key={p} className="text-[0.6rem] lk-border rounded px-1.5 py-0.5 text-[var(--lk-muted)]">{PLATFORM_LABELS[p] || p}</span>
                                                    ))}
                                                    {run.created_at && (
                                                        <span className="text-[0.6rem] text-[var(--lk-muted)] flex items-center gap-1">
                                                            <Clock className="w-2.5 h-2.5" />
                                                            {new Date(run.created_at).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {run.quality_score != null && (
                                                <div className="shrink-0 text-right">
                                                    <div className="font-mono text-sm text-[var(--lk-primary)]">{Math.round(run.quality_score)}</div>
                                                    <div className="lk-label">score</div>
                                                </div>
                                            )}
                                            <ArrowRight className="w-4 h-4 text-[var(--lk-muted)] shrink-0 hidden sm:block" />
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
